import { captureError } from './sentry';
import { getErrorMessage } from './sync/error-utils';

/**
 * Maps technical error messages to user-friendly messages
 */
function sanitizeErrorMessage(error: unknown): string {
  const rawMessage = getErrorMessage(error, '').toLowerCase();

  // Database/IndexedDB errors
  if (
    rawMessage.includes('database') ||
    rawMessage.includes('indexeddb') ||
    rawMessage.includes('dexie') ||
    rawMessage.includes('quotaexceeded') || // cspell:ignore quotaexceeded
    rawMessage.includes('constraint')
  ) {
    return 'Unable to save data. Please try again.';
  }

  // Network errors
  if (
    rawMessage.includes('network') ||
    rawMessage.includes('fetch') ||
    rawMessage.includes('connection') ||
    rawMessage.includes('timeout') ||
    rawMessage.includes('failed to fetch')
  ) {
    return 'Connection problem. Please check your internet.';
  }

  // Authentication errors (keep as-is, they're usually already user-friendly)
  if (
    rawMessage.includes('auth') ||
    rawMessage.includes('unauthorized') ||
    rawMessage.includes('forbidden') ||
    rawMessage.includes('invalid verification code') ||
    rawMessage.includes('email')
  ) {
    // Return original message if it's already user-friendly, otherwise use generic
    const original = getErrorMessage(error, '');
    if (original.length > 0 && original.length < 100) {
      return original;
    }
    return 'Authentication error. Please try again.';
  }

  // Validation errors (keep as-is, they're already user-friendly)
  if (rawMessage.includes('validation') || rawMessage.includes('invalid')) {
    return getErrorMessage(error, 'Invalid input. Please check your data.');
  }

  // Generic fallback
  return 'Something went wrong. Please try again.';
}

interface ErrorContext {
  operation?: string;
  userId?: string;
  [key: string]: unknown;
}

/**
 * Handle errors that should be shown to users
 * Use for: form validation, critical operations that block user flow, unexpected behavior requiring explanation
 *
 * @param error - The error to handle
 * @param userMessage - Optional user-friendly message (will be sanitized if not provided)
 * @param context - Additional context for Sentry
 * @returns User-friendly error message
 */
export function handleUserFacingError(
  error: unknown,
  userMessage?: string,
  context?: ErrorContext,
): string {
  const sanitizedMessage = userMessage || sanitizeErrorMessage(error);
  const rawError = getErrorMessage(error, 'Unknown error');

  // Log to console (both dev and prod) with better error details
  if (error instanceof Error) {
    console.error('[User-facing error]', sanitizedMessage, {
      message: error.message,
      stack: error.stack,
      name: error.name,
      context,
    });
  } else {
    console.error('[User-facing error]', sanitizedMessage, {
      error: String(error),
      errorType: typeof error,
      rawError,
      context,
    });
  }

  // Send to Sentry (captureError handles production-only check)
  // User-facing errors are critical and should always be tracked
  captureError(error, {
    ...context,
    userFacing: true,
    userMessage: sanitizedMessage,
    rawError,
  });

  return sanitizedMessage;
}

/**
 * Handle errors that should NOT be shown to users
 * Use for: background sync failures, optional data loading, non-blocking operations
 *
 * @param error - The error to handle
 * @param context - Additional context for Sentry
 * @param logLevel - Log level ('error' or 'warn', default: 'error')
 */
export function handleBackgroundError(
  error: unknown,
  context?: ErrorContext,
  logLevel: 'error' | 'warn' = 'error',
): void {
  const rawError = getErrorMessage(error, 'Unknown error');

  // Log to console (both dev and prod)
  if (logLevel === 'error') {
    console.error('[Background error]', rawError, { error, context });
  } else {
    console.warn('[Background warning]', rawError, { error, context });
  }

  // Send to Sentry (captureError handles production-only check)
  // Background errors help debug unexpected issues
  captureError(error, {
    ...context,
    userFacing: false,
    logLevel,
    rawError,
  });
}

/**
 * Get user-friendly error message without handling (for cases where you need just the message)
 */
export function getUserFriendlyMessage(error: unknown, fallback?: string): string {
  if (fallback) {
    return fallback;
  }
  return sanitizeErrorMessage(error);
}
